import React, { useEffect, useState } from "react";
import "../../App.css";
import "../../profile.css";
import {
  MoreVertical,
  Lock,
  Eye,
  Download,
  Share2,
  X,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import service from "../../services/service";
import endpoints from "../../services/endpoints";

const RewardsPostcards = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [challengeData, setChallengeData] = useState(null);
  const [postcards, setPostcards] = useState([]);
  const [activeMenu, setActiveMenu] = useState(null);

  // Modal States
  const [isCertModalOpen, setIsCertModalOpen] = useState(false);
  const [isDownloadingCert, setIsDownloadingCert] = useState(false);

  // Combined preview + detail modal
  const [selectedCard, setSelectedCard] = useState(null);
  const [showCheckpointModal, setShowCheckpointModal] = useState(false);

  useEffect(() => {
    fetchPostcards();
  }, [id]);

  // Global click handler to close dropdowns when clicking outside
  useEffect(() => {
    const handleOutside = (e) => {
      if (!e.target.closest(".rewardsPostcards-menuWrapper")) {
        setActiveMenu(null);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const fetchPostcards = async () => {
    try {
      setLoading(true);
      const response = await service.get(endpoints.rewards.postcardList(id));
      const data = response?.data?.data || response?.data || {};

      setChallengeData(data);
      if (Array.isArray(data.postcards)) {
        setPostcards(data.postcards);
      } else {
        setPostcards([]);
      }
    } catch (err) {
      console.log(err);
      setChallengeData(null);
      setPostcards([]);
    } finally {
      setLoading(false);
    }
  };

  const getCheckpointImage = (card) =>
    card?.milestoneImage || card?.postcardImageUrl || null;

  const getCheckpointTitle = (card) =>
    card?.postcardTitle || card?.milestoneTitle || "Checkpoint";

  const handleDownloadCertificate = async () => {
    try {
      setIsDownloadingCert(true);
      const response = await service.get(
        endpoints.rewards.downloadCertificate(id),
      );
      const data = response?.data?.data || response?.data;

      if (data?.completed && data?.certificateUrl) {
        window.open(data.certificateUrl, "_blank");
      } else {
        setIsCertModalOpen(true);
      }
    } catch (err) {
      console.error("Error fetching certificate status", err);
      setIsCertModalOpen(true);
    } finally {
      setIsDownloadingCert(false);
    }
  };

  const handleOpenCheckpoint = (card) => {
    setSelectedCard(card);
    setShowCheckpointModal(true);
  };

  const handleDownload = async (card) => {
    if (!card.postcardImageUrl) return;

    try {
      const response = await fetch(card.postcardImageUrl, {
        mode: "cors",
        cache: "no-store",
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${card.postcardTitle || "checkpoint"}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed, opening in new tab instead", error);
      window.open(card.postcardImageUrl, "_blank");
    }
  };

  const handleShare = async (card) => {
    if (!card?.postcardImageUrl) return;

    const shareData = {
      title: card.postcardTitle || "My Achievement",
      text: `🎉 I just unlocked "${card.postcardTitle}"! Check it out.`,
      url: card.postcardImageUrl,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(card.postcardImageUrl);
        alert("Checkpoint link copied to clipboard!");
      }
    } catch (error) {
      console.error("Share cancelled or failed", error);
    }
  };

  const menuItems = (card) => [
    {
      icon: <Eye size={17} />,
      label: "View Checkpoint",
      show: true,
      onClick: () => handleOpenCheckpoint(card),
    },
    {
      icon: <Download size={17} />,
      label: "Download",
      show: !!card?.postcardImageUrl,
      onClick: () => handleDownload(card),
    },
    {
      icon: <Share2 size={17} />,
      label: "Share",
      show: true,
      onClick: () => handleShare(card),
    },
  ];

  const totalMilestones = postcards.length;
  const completedMilestones = postcards.filter((p) => p.isUnlocked).length;
  const remainingMilestones = totalMilestones - completedMilestones;
  const progressPercentage =
    totalMilestones === 0 ? 0 : (completedMilestones / totalMilestones) * 100;

  if (loading)
    return (
      <div className="rewardsPostcards-container">
        <div className="rewardsPostcards-empty">Loading checkpoints...</div>
      </div>
    );
  if (!challengeData)
    return (
      <div className="rewardsPostcards-container">
        <div className="rewardsPostcards-empty">Failed to load checkpoints.</div>
      </div>
    );

  return (
    <div className="rewardsPostcards-container">
      <div className="rewardsPostcards-header">
        <div className="rewardsPostcards-headerLeft">
          <button
            className="rewardsPostcards-backBtn"
            onClick={() => navigate(-1)}
          >
            ←
          </button>
          <div>
            <h1>{challengeData?.challengeName || "Checkpoints"}</h1>
          </div>
        </div>

        <button
          className="rewardsPostcards-certificateBtn"
          onClick={handleDownloadCertificate}
          disabled={isDownloadingCert}
        >
          {isDownloadingCert ? "Checking..." : "Download Certificate"}
        </button>
      </div>

      {postcards.length === 0 ? (
        <div className="rewardsPostcards-empty">No checkpoints available.</div>
      ) : (
        <div className="rewardsPostcards-grid">
          {postcards.map((card) => {
            const checkpointImage = getCheckpointImage(card);
            const checkpointTitle = getCheckpointTitle(card);

            return (
              <div key={card.milestoneId} className="rewardsPostcards-card">
                <div className="rewardsPostcards-top">
                  <div
                    className="rewardsPostcards-left rewardsPostcards-leftClickable"
                    onClick={() => handleOpenCheckpoint(card)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleOpenCheckpoint(card);
                      }
                    }}
                  >
                    <div className="rewardsPostcards-icon rewardsPostcards-iconLarge">
                      {checkpointImage ? (
                        <img
                          src={checkpointImage}
                          alt={checkpointTitle}
                          className="rewardsPostcards-thumbImg"
                        />
                      ) : (
                        <img src="/challenge_icon.svg" alt="icon" />
                      )}
                    </div>
                    <div>
                      <h3 className="rewardsPostcards-titleSmall">
                        {checkpointTitle}
                      </h3>
                      <span>
                        {card.remainingDistanceKm} km to next checkpoint
                      </span>
                    </div>
                  </div>

                  {card.isUnlocked && (
                    <div className="rewardsPostcards-menuWrapper">
                      <button
                        className="rewardsPostcards-menuBtn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMenu(
                            activeMenu === card.milestoneId
                              ? null
                              : card.milestoneId,
                          );
                        }}
                      >
                        <MoreVertical size={18} />
                      </button>

                      {activeMenu === card.milestoneId && (
                        <div className="rewardsPostcards-dropdown">
                          {menuItems(card)
                            .filter((item) => item.show)
                            .map((item) => (
                              <button
                                key={item.label}
                                className="rewardsPostcards-dropdownItem"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  item.onClick();
                                  setActiveMenu(null);
                                }}
                              >
                                {item.icon}
                                <span>{item.label}</span>
                              </button>
                            ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="rewardsPostcards-divider" />

                {card.isUnlocked ? (
                  <div className="rewardsPostcards-unlocked">
                    <Lock size={15} />
                    <span>
                      {card.unlockText?.replace(/Postcard/gi, "Checkpoint") ||
                        "Checkpoint Unlocked"}
                    </span>
                  </div>
                ) : (
                  <div className="rewardsPostcards-locked">
                    <Lock size={15} />
                    <span>
                      Complete {card.remainingDistanceKm} KM to unlock checkpoint
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* --- CERTIFICATE LOCKED MODAL --- */}
      {isCertModalOpen && (
        <div className="custom-modal-overlay">
          <div className="custom-modal-content cert-modal-content">
            <button
              className="custom-modal-close"
              onClick={() => setIsCertModalOpen(false)}
            >
              <X size={16} />
            </button>
            <div className="cert-modal-icon-wrapper">
              <div className="cert-modal-icon">🏆</div>
            </div>
            <div className="cert-modal-badge">CERTIFICATE LOCKED</div>
            <h2 className="cert-modal-title">Oops! Not quite there</h2>

            <p className="cert-modal-description">
              {remainingMilestones === 0 ? (
                <>
                  Complete the challenge to unlock and download your certificate.
                  You're almost at the finish line! 🏁
                </>
              ) : (
                <>
                  Complete all checkpoints to unlock and download your
                  certificate. You're almost there —{" "}
                  <span className="highlight-red">
                    {remainingMilestones} checkpoint
                    {remainingMilestones !== 1 ? "s" : ""} remaining!
                  </span>
                </>
              )}
            </p>

            <div className="cert-modal-progress-box">
              <div className="cert-modal-progress-header">
                <span>Overall progress</span>
                <span className="highlight-red">
                  {completedMilestones} / {totalMilestones} done
                </span>
              </div>
              <div className="cert-modal-progress-bar-bg">
                <div
                  className="cert-modal-progress-bar-fill"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
            <div className="cert-modal-actions">
              <button
                className="cert-modal-btn-primary"
                onClick={() => setIsCertModalOpen(false)}
              >
                Keep going 💪
              </button>
              <button
                className="cert-modal-btn-secondary"
                onClick={() => setIsCertModalOpen(false)}
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- COMBINED CHECKPOINT PREVIEW + DETAIL MODAL --- */}
      {showCheckpointModal && selectedCard && (
        <div
          className="custom-modal-overlay"
          onClick={() => setShowCheckpointModal(false)}
        >
          <div
            className="custom-modal-content checkpoint-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="custom-modal-close"
              onClick={() => setShowCheckpointModal(false)}
            >
              <X size={16} />
            </button>

            <div className="checkpoint-modal-header">
              <h2 className="cert-modal-title">
                {getCheckpointTitle(selectedCard)}
              </h2>
              {selectedCard.status && (
                <span
                  className={`status-badge ${selectedCard.status.toLowerCase()}`}
                >
                  {selectedCard.status}
                </span>
              )}
            </div>

            <div className="checkpoint-modal-body">
              <div className="checkpoint-modal-imageWrap">
                {getCheckpointImage(selectedCard) ? (
                  <img
                    src={getCheckpointImage(selectedCard)}
                    alt={getCheckpointTitle(selectedCard)}
                    className="checkpoint-modal-image"
                  />
                ) : (
                  <p className="modal-error-text">
                    No image available for this checkpoint.
                  </p>
                )}
              </div>

              <div className="checkpoint-modal-details">
                <h4 className="checkpoint-modal-descLabel">
                  Checkpoint Description
                </h4>
                <p className="detail-description">
                  {selectedCard.milestoneDescription ||
                    "No description provided."}
                </p>

                <div className="detail-stats-grid checkpoint-modal-stats">
                  <div className="detail-stat-box">
                    <span className="stat-label">Km to next checkpoint</span>
                    <span className="stat-value">
                      {selectedCard.remainingDistanceKm} KM
                    </span>
                  </div>
                </div>

                <div className="checkpoint-modal-actions">
                  {selectedCard.isUnlocked && selectedCard.postcardImageUrl && (
                    <button
                      className="cert-modal-btn-primary"
                      onClick={() => handleDownload(selectedCard)}
                    >
                      Download
                    </button>
                  )}
                  {selectedCard.isUnlocked && selectedCard.postcardImageUrl && (
                    <button
                      className="cert-modal-btn-secondary"
                      onClick={() => handleShare(selectedCard)}
                    >
                      Share
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RewardsPostcards;
